import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, H5, Thumbnail, Icon } from 'design-system-web';
import { ErrorMessage, Loading } from 'components';
import { Text } from 'components/typography';
import { useTranslation } from 'react-i18next';
import type { Channel } from 'application-types';
import {
  ICON_SET_FONTAWESOME,
  FONT_AWESOME_REGULAR,
} from 'lane-shared/helpers/constants/icons';
import { imageUrl } from 'lane-shared/helpers/formatters';
import { useOnboardingBuildings, Edge as BuildingEdge } from 'lane-shared/contexts/SignUpContext/useOnboardingBuildings';
import { useOnboardingCompanies, Edge as CompanyEdge } from 'lane-shared/contexts/SignUpContext/useOnboardingCompanies';
import { useOnboardingAnalytics } from 'lane-shared/hooks/analytics';
import { OnboardingBuildingDropDownInput } from './OnboardingBuildingDropDownInput';
import { OnboardingCompanyDropDownInput } from './OnboardingCompanyDropDownInput';
import { convertCompanyEdgeToChannelType } from 'components/onboarding/components/ChannelSearchCircleListWrapper/ChannelSearchCircleListWrapper';
import { Header } from '../components/Header';
import { OnboardingCompleteData } from '..';
import { CantFindCompany } from '../../../components/CantFindCompany/CantFindCompany';
import { FeatureFlag } from 'constants-flags';
import { useFlag } from 'lane-shared/hooks';


import styles from './index.scss';

const TRANSLATION_KEYS = {
  buildingLabel: 'web.onboarding.mergebuilding.location.title',
  companyLabel: 'shared.onboarding.company.name.search.title',
  finishButton: 'shared.onboarding.finishButton',
  serverError: 'web.onboarding.building.server.error',
  backButton: 'shared.onboarding.backButton',
  companySelectionRequiredNoCompanies: 'shared.onboarding.companySelectionRequiredNoCompanies'
};

type Props = {
  userName: string;
  onboardingState: 'loading' | 'idle' | 'error';
  errorMessage?: string;
  onComplete: (data: OnboardingCompleteData) => void;
  onBuildingComplete: (building: Channel, fullName: string) => void;
  onClear: () => void;
  invitedBuilding?: Channel;
  invitedCompany?: Channel;
  presumedEmailDomain?: string;
  onCantFindCompany: (building: Channel, fullName: string) => void;
  companySelectionRequiredDuringSignup?: boolean;
};

export const OnboardingBuildingAndCompany = ({
  userName,
  errorMessage,
  onboardingState,
  onComplete,
  onBuildingComplete,
  onClear,
  invitedBuilding,
  invitedCompany,
  presumedEmailDomain,
  onCantFindCompany,
  companySelectionRequiredDuringSignup = false,
}: Props) => {

  const { t } = useTranslation();
  const { onboardingTracker } = useOnboardingAnalytics();
  const companySelectionFFOn = useFlag(
    FeatureFlag.CompanySelectionRequiredDuringSignup,
    false
  );
  const companySelectionRequired =
    companySelectionRequiredDuringSignup && companySelectionFFOn;

  const [companySearchText, setCompanySearchText] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState<Channel | null>(
    invitedBuilding ?? null
  );
  const [selectedCompany, setSelectedCompany] = useState<Channel | null>(
    invitedCompany ?? null
  );

  // Track if we've already called onComplete to prevent duplicate calls
  const hasCompletedRef = useRef(false);

  // Check if user is invited with assigned building and/or company
  const isBuildingReadOnly = Boolean(invitedBuilding);
  const isCompanyReadOnly = Boolean(invitedBuilding && invitedCompany);

  // Fetch buildings
  const {
    buildings,
    isInitialLoading: isLoadingBuildings,
    error: buildingError,
  } = useOnboardingBuildings({
    query: '',
    presumedEmailDomain,
    skip: isBuildingReadOnly,
    pageSize: 1000 // TODO: Remove this once we have a proper pagination
  });

  // Fetch companies based on selected building
  const {
    companies,
    isInitialLoading: isLoadingCompanies,
    isFetchingMore: isFetchingMoreCompanies,
    hasNextPage: hasNextPageCompanies,
    fetchMore: fetchMoreCompanies,
    error: companyError,
    refetch: refetchCompanies,
  } = useOnboardingCompanies({
    query: companySearchText,
    buildingId: selectedBuilding?._id ?? null,
    presumedEmailDomain,
    pageSize: 1000 // TODO: Remove this once we have a proper pagination
  });

  // Show server errors
  useEffect(() => {
    if (buildingError || companyError) {
      window.Toast.show(<Text>{t(TRANSLATION_KEYS.serverError)}</Text>);
    }
  }, [buildingError, companyError, t]);

  // Auto-select company if there's only 1 company for selected building
  useEffect(() => {
    if (
      !isCompanyReadOnly &&
      selectedBuilding &&
      !isLoadingCompanies &&
      companies.length === 1 &&
      !selectedCompany
    ) {
      const autoSelectedCompany = convertCompanyEdgeToChannelType(
        companies[0]
      );

      setSelectedCompany(autoSelectedCompany);
    }
  }, [
    companies,
    isLoadingCompanies,
    selectedBuilding,
    isCompanyReadOnly,
    selectedCompany,
  ]);



  const handleBuildingSelectionChange = (building: Channel | null) => {
    setSelectedBuilding(building);
    setSelectedCompany(null); // Reset company when building changes

    if (building) {
      onboardingTracker.Select.Building({ buildingId: building._id });

      setCompanySearchText('');
    } else {
      onClear();

      if (selectedBuilding) {
        onboardingTracker.Remove.Building({ buildingId: selectedBuilding._id });
      }
    }
  };


  const handleCompanySelectionChange = (company: Channel | null) => {
    setSelectedCompany(company);

    if (company) {
      onboardingTracker.Select.Company({ companyId: company._id });
    } else {
      onClear();

      if (selectedCompany) {
        onboardingTracker.Remove.Company({ companyId: selectedCompany._id });
      }
    }
  };

  const handleComplete = useCallback((building: Channel | null, company: Channel | null) => {
    if (building && !company) {
      onBuildingComplete(building, userName);
    } else if (building && company) {
        onComplete({
          building,
          company,
          userName,
        });
    }
  }, [onBuildingComplete, onComplete, userName]);

  
  const isOnboardingStateLoading = onboardingState === 'loading';
  const noEligibleCompanies =
    Boolean(selectedBuilding) &&
    !isLoadingCompanies &&
    companies.length === 0;
  const isSubmitDisabled =
    !selectedBuilding ||
    isOnboardingStateLoading ||
    (companySelectionRequired
      ? !selectedCompany || noEligibleCompanies
      : isLoadingCompanies || (!selectedCompany && companies.length > 0));

  const renderCompanySelection = () => {
    return (
      <div className={styles.searchContainer}>
        <OnboardingCompanyDropDownInput
          data={{
            isReadOnly: isCompanyReadOnly || (companies.length === 1 && !companySearchText),
            signupError: null,
            building: selectedBuilding,
            company: selectedCompany,
            onboardingCompaniesResult: {
              companies,
              isInitialLoading: isLoadingCompanies,
              isFetchingMore: isFetchingMoreCompanies,
              error: companyError,
              fetchMore: fetchMoreCompanies,
              hasNextPage: hasNextPageCompanies,
              refetch: refetchCompanies,
            },
          }}
          handlers={{
            resetSelection: () => {
              handleCompanySelectionChange(null);
            },
            onParentCompanySelectionChange: (company: Channel) => {
              handleCompanySelectionChange(company);
            },
            renderSelectedLocation,
          }}
        />
      </div>
    );
  };


  const handleCantFindCompany = () => {
    if (selectedBuilding) {
      onCantFindCompany(selectedBuilding, userName);
    }
  };

  
  const isLoading = isOnboardingStateLoading || (invitedBuilding && isLoadingCompanies);

  // Handle automatic completion when conditions are met
  useEffect(() => {
    const shouldRedirectDirectlyToLogin =
      !errorMessage &&
      !isLoading &&
      invitedBuilding &&
      companies.length <= 1 &&
      !hasCompletedRef.current;

    if (shouldRedirectDirectlyToLogin) {
      const building = invitedBuilding;
      const company = companies?.[0]
        ? convertCompanyEdgeToChannelType(companies[0])
        : null;
        
      handleComplete(building, company);
      hasCompletedRef.current = true;
    }
  }, [errorMessage, isLoading, invitedBuilding, companies, userName, handleComplete]);

  const showCompanySelectionRequiredMessage =
    companySelectionRequired && noEligibleCompanies;

  
  const renderSelectedLocation = (selectedBuilding: BuildingEdge | CompanyEdge) => {
    const thumbnailUrl = selectedBuilding.node?.thumbnailUrl ? imageUrl(selectedBuilding.node?.thumbnailUrl) : null;
    const inviteOnly = selectedBuilding.node?.inviteOnly;

    return (
      <span className={styles.selectedLocation}>
        {thumbnailUrl ? (
          <Thumbnail
            src={thumbnailUrl}
            name={selectedBuilding.node?.name ?? ''}
            className={styles.selectedLocationIcon}
          />
        ) : (
          <Icon
            name={inviteOnly ? 'lock' : 'map-marker-alt'}
            set={ICON_SET_FONTAWESOME}
            type={FONT_AWESOME_REGULAR}
            size="small"
          />
        )}
        <span>{selectedBuilding.node?.name}</span>
      </span>
    );
  };

  return (
    <div className={styles.container}>
      <Header userName={userName} />
      {(errorMessage || showCompanySelectionRequiredMessage) && (
        <ErrorMessage
          error={
            showCompanySelectionRequiredMessage
              ? t(TRANSLATION_KEYS.companySelectionRequiredNoCompanies)
              : errorMessage
          }
          />
        )}
      
      {isLoading ? (
        <Loading className={styles.loading} />
      ) : (
        <div className={styles.contentContainer}>
          {/* Building Selection */}
          <div className={styles.sectionContainer}>
            <H5 className={styles.title}>
              {t(TRANSLATION_KEYS.buildingLabel)}
            </H5>
            <div className={styles.searchContainer}>
                <OnboardingBuildingDropDownInput
                  data={{
                    isReadOnly: isBuildingReadOnly,
                    building: selectedBuilding,
                    buildings,
                  }}
                  config={{
                    isLoading: isLoadingBuildings,
                  }}
                  handlers={{
                    onBuildingSelectionChange: handleBuildingSelectionChange,
                    renderSelectedLocation,
                  }}
                />
              </div>
          </div>

          {/* Company Selection */}
          {selectedBuilding && (companies.length > 0 || companySearchText) && (
            <div className={styles.sectionContainer}>
              <H5 className={styles.title}>
                {t(TRANSLATION_KEYS.companyLabel)}
              </H5>
              {renderCompanySelection()}
            </div>
          )}

          {/* Submit Button */}
          <Button
            loading={isOnboardingStateLoading}
            className={styles.submitButton}
            fullWidth
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              handleComplete(selectedBuilding, selectedCompany);
            }}
            size="large"
            type="submit"
            disabled={isSubmitDisabled || isOnboardingStateLoading}
          >
            {t(TRANSLATION_KEYS.finishButton)}
          </Button>
         
            {selectedBuilding && companies.length > 0 && <CantFindCompany
        parentCompany={selectedBuilding}
              invitedChannelId={selectedBuilding?._id ?? null}
              onClick={handleCantFindCompany}
            />
          }
        </div>
      )}
    </div>
  );
};

